import { RequireAccess } from '@/components/require-access'
import { centsToEuros, maxCampaignDurationDays } from '@/constants'
import { useQuotaRequestMore } from '@/hooks'
import { getLocale } from '@/hooks/helpers'
import { countryCodeToFlag, formatPeriodEnd, useEntitlements } from '@/lib'
import { Button, DateRangeField, SuggestionField } from '@/ui'
import { CampaignTargetingViewModel } from '@/ui'
import { CityLocationMap } from '@/ui'
import { CampaignNav } from '@/ui'
import { WidgetInputToggleField } from '@/ui/rapid-ui/inputs/widgets/widget-input-toggle-field'
import type { RegionData } from '@jobilla/nexus-sdk'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

function formatReachNumber(num: number): string {
	return new Intl.NumberFormat(getLocale(), {
		notation: 'compact',
		compactDisplay: 'short',
		maximumFractionDigits: 1,
	}).format(num)
}

export function CampaignTargeting<
	TInterests extends Record<string, unknown>,
	TCountries extends Record<string, unknown>,
	TCities extends
		& {
			radius: number
			country: { id: string; name: string } | null
			region: RegionData | null
			primaryCity?: string | null
		}
		& Record<string, unknown>,
>(
	props: CampaignTargetingViewModel<TInterests, TCountries, TCities>,
) {
	const { t } = useTranslation('editor')
	const { campaignQuota } = useEntitlements()
	const requestMore = useQuotaRequestMore(campaignQuota.periodEnd)
	const { view, events, options } = props

	const hasInterests = view.interests.length > 0
	const hasCountries = view.countries.length > 0
	const hasCities = view.cities.length > 0

	// Check for invalid date range
	const isInvalidDateRange = view.startDate !== null && view.endDate !== null && view.endDate <= view.startDate

	// Check for invalid combinations
	const isInterestOnly = hasInterests && !hasCountries && !hasCities
	const hasCountriesAndCities = !hasInterests && hasCountries && hasCities
	const hasAllThree = hasInterests && hasCountries && hasCities
	const hasNone = !hasInterests && !hasCountries && !hasCities

	const isInvalidCombination = isInvalidDateRange || isInterestOnly || hasCountriesAndCities || hasAllThree || hasNone
		|| campaignQuota.isExhausted

	const formattedPeriodEnd = formatPeriodEnd(campaignQuota.periodEnd)

	const getValidationErrorMessage = () => {
		if (campaignQuota.isExhausted) {
			return formattedPeriodEnd
				? t('common:campaignQuota.exhaustedWithDate', { date: formattedPeriodEnd })
				: t('common:campaignQuota.exhausted')
		}
		if (isInvalidDateRange) { return t('targeting.validation.endDateAfterStart') }
		if (hasNone) { return t('targeting.validation.selectTargeting') }
		if (isInterestOnly) { return t('targeting.validation.interestsNeedLocation') }
		if (hasCountriesAndCities) {
			return t('targeting.validation.countriesAndCitiesConflict')
		}
		if (hasAllThree) { return t('targeting.validation.interestsWithEither') }
		return null
	}

	const validationError = getValidationErrorMessage()
	const canConfigureTargeting = props.common.permissions.canConfigureTargeting

	return (
		<CampaignNav activeCampaignNavLink="targeting" whiteBackground>
			<div className="pr-8 pl-4 py-4">
				{campaignQuota.isExhausted && (
					<div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
						{formattedPeriodEnd
							? t('common:campaignQuota.exhaustedWithDate', { date: formattedPeriodEnd })
							: t('common:campaignQuota.exhausted')}
						{' · '}
						<button
							type="button"
							onClick={requestMore.request}
							disabled={requestMore.isDisabled}
							className="cursor-pointer underline underline-offset-2 hover:no-underline disabled:cursor-default disabled:no-underline disabled:opacity-50"
						>
							{requestMore.isError
								? t('common:campaignQuota.requestFailed')
								: requestMore.hasSent
								? t('common:campaignQuota.requestSent')
								: t('common:campaignQuota.requestMore')}
						</button>
					</div>
				)}
				{!canConfigureTargeting
					? (
						<div className="flex flex-col items-center justify-center py-10 text-center gap-4">
							<h3 className="text-lg font-semibold text-gray-800">
								{t('targeting.notConfigurable')}
							</h3>
							<p className="text-sm text-gray-600 max-w-sm">
								{t('targeting.notConfigurableDesc')}
							</p>
						</div>
					)
					: view.isCampaignQueued
					? (
						<div className="flex flex-col items-center justify-center py-10 text-center gap-4">
							<h3 className="text-lg font-semibold text-gray-800">
								{t('targeting.campaignPushedToMeta')}
							</h3>
							{view.hasUrlError && !view.isLoadingUrl
								? (
									<p className="text-sm text-red-500 max-w-sm">
										{t('targeting.couldNotLoadDetails')}
									</p>
								)
								: (
									<p className="text-sm text-gray-600 max-w-sm">
										{t('targeting.manageOnFacebook')}
									</p>
								)}
							<Link
								target="_blank"
								rel="noopener noreferrer"
								className="text-blue-600 hover:underline"
								disabled={view.isLoadingUrl
									|| view.hasUrlError
									|| !view.manageCampaignUrl}
								to={view.manageCampaignUrl ?? ''}
							>
								{view.isLoadingUrl
									? t('targeting.loading')
									: t('targeting.goToFacebookAdsManager')}
							</Link>
						</div>
					)
					: (
						<div className="grid grid-cols-2 gap-8 mt-4">
							<div className="col-span-2">
								{view.startDate && view.endDate
									? (
										<>
											<DateRangeField
												label={t('targeting.campaignDates')}
												value={[view.startDate, view.endDate]}
												onChange={([start, end]) => {
													events.onStartDateChange(start)
													events.onEndDateChange(end)
												}}
												minDate={new Date()}
												maxDurationDays={maxCampaignDurationDays}
												readOnly={!view.overrideDatesEnabled}
												readOnlyReason={t('targeting.datesLockedUntilOverride')}
											/>
											<div className="flex items-center gap-2 mt-2">
												<WidgetInputToggleField
													value={view.overrideDatesEnabled}
													onChange={events.onToggleOverrideDates}
													ariaLabel={t('targeting.overrideDates')}
												/>
												<span className="text-sm text-grey-600">
													{t('targeting.overrideDates')}
												</span>
											</div>
										</>
									)
									: <div className="h-14 w-full bg-grey-100 rounded-lg animate-pulse" />}
							</div>

							<RequireAccess permission="campaign:view-budget">
								<div className="col-span-2">
									<div className="space-y-2">
										<h3 className="text-sm font-medium text-gray-700">
											{t('targeting.budgetBreakdown')}
										</h3>
										{view.budgetBreakdown
											? (
												<div className="p-4 w-1/2 bg-gray-50 rounded-lg border border-gray-200">
													<div className="space-y-2 text-sm text-gray-700">
														<div className="flex justify-between">
															<span>{t('targeting.metaBudget')}</span>
															<span className="font-medium">
																€{centsToEuros(view.budgetBreakdown.metaBudget).toFixed(
																	2,
																)} (
																{100 - view.serviceFeePercent}%)
															</span>
														</div>
														<div className="flex justify-between">
															<span>{t('targeting.serviceFee')}</span>
															<span className="font-medium">
																€{centsToEuros(view.budgetBreakdown.serviceFee).toFixed(
																	2,
																)} (
																{view.serviceFeePercent}%)
															</span>
														</div>
														{view.addons.map((addon) => (
															<div key={addon.id} className="flex justify-between">
																<span>{addon.name}:</span>
																<span className="font-medium">
																	€{centsToEuros(addon.price).toFixed(2)}
																</span>
															</div>
														))}
														<div className="flex justify-between pt-2 mt-2 border-t border-gray-300 font-semibold text-gray-900">
															<span>{t('targeting.totalBudget')}</span>
															<span>€{centsToEuros(view.budget).toFixed(2)}</span>
														</div>
													</div>
												</div>
											)
											: (
												<div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
													<div className="text-sm text-gray-500">
														{t('targeting.budgetNotAvailable')}
													</div>
												</div>
											)}
									</div>
								</div>
							</RequireAccess>

							<div className="col-span-2">
								<SuggestionField
									label={t('targeting.interests')}
									values={view.interests}
									onChange={events.onInterestsChange}
									search={view.interestSearch}
									onSearchChange={events.onInterestSearchChange}
									suggestions={view.interestSuggestions}
									renderValue={(interest) => interest.name}
									placeholder={t('targeting.noInterests')}
									isLoading={view.isLoadingInterests}
									emptyMessage={t('targeting.noInterestsFound')}
									hasError={view.hasInterestsError}
									errorMessage={t('targeting.failedToLoadInterests')}
									getKey={(interest) => interest.id}
								/>
							</div>
							<div className="col-span-2">
								<SuggestionField
									label={t('targeting.countries')}
									values={view.countries}
									onChange={events.onCountriesChange}
									search={view.countrySearch}
									onSearchChange={events.onCountrySearchChange}
									suggestions={view.countrySuggestions}
									renderValue={(country) => country.name}
									placeholder={t('targeting.noCountries')}
									isLoading={view.isLoadingCountries}
									emptyMessage={t('targeting.noCountriesFound')}
									hasError={view.hasCountriesError}
									errorMessage={t('targeting.failedToLoadCountries')}
									getKey={(country) => country.id}
								/>
							</div>
							<div className="col-span-2">
								<SuggestionField
									label={t('targeting.cities')}
									hint={t('targeting.cityRadiusHint')}
									values={view.cities}
									onChange={events.onCitiesChange}
									search={view.citySearch}
									onSearchChange={events.onCitySearchChange}
									suggestions={view.citySuggestions}
									renderValue={(city) => {
										const cityParts = [city.name]
										if (city.region?.name && city.region.name !== city.name) {
											cityParts.push(city.region.name)
										}
										const flag = city.country?.id ? countryCodeToFlag(city.country.id) : ''
										return (
											<span>
												{flag && <span className="mr-1">{flag}</span>}
												{cityParts.join(', ')}
												{city.country?.name && (
													<span className="text-grey-500">— {city.country.name}</span>
												)}
											</span>
										)
									}}
									renderSuggestion={(city) => {
										const cityParts = [city.name]
										if (city.primaryCity && city.primaryCity !== city.name) {
											cityParts.push(city.primaryCity)
										}
										if (city.region?.name && city.region.name !== city.name) {
											cityParts.push(city.region.name)
										}
										const flag = city.country?.id ? countryCodeToFlag(city.country.id) : ''
										return (
											<span>
												{flag && <span className="mr-1">{flag}</span>}
												{cityParts.join(', ')}
												{city.country?.name && (
													<span className="text-grey-500">— {city.country.name}</span>
												)}
											</span>
										)
									}}
									placeholder={t('targeting.noCities')}
									numericEditorAccessor={(city) => city.radius}
									numericEditorSuffix="km"
									numericLimits={options.cityRadiusLimits}
									numericEditorMutator={(radius, city) => ({ ...city, radius })}
									isLoading={view.isLoadingCities}
									emptyMessage={t('targeting.noCitiesFound')}
									hasError={view.hasCitiesError}
									errorMessage={t('targeting.failedToLoadCities')}
									getKey={(city) => city.id}
								/>
							</div>
							{hasCities && (
								<div className="col-span-2">
									<div className="text-sm font-medium text-grey-700 mb-2">
										{t('targeting.selectedLocations')}
									</div>
									<CityLocationMap
										cities={view.cities.map((city) => ({
											id: city.id,
											name: city.name,
											radius: city.radius,
											country: city.country,
										}))}
										height={350}
									/>
								</div>
							)}
							{(view.reachEstimate
								|| view.isLoadingReachEstimate
								|| view.hasReachEstimateError
								|| view.interests.length > 0
								|| view.countries.length > 0
								|| view.cities.length > 0) && (
								<div className="col-span-2 p-4 bg-gray-50 rounded-lg">
									<h3 className="text-sm font-medium text-gray-700 mb-2">
										{t('targeting.estimatedReach')}
									</h3>
									{view.hasReachEstimateError
										? (
											<div className="text-sm text-red-500">
												{t('targeting.failedToLoadReach')}
											</div>
										)
										: view.isLoadingReachEstimate
										? <div className="text-sm text-gray-500">{t('targeting.loading')}</div>
										: view.reachEstimate
										? (
											<div className="text-sm text-gray-600">
												{formatReachNumber(view.reachEstimate.min)} -{' '}
												{formatReachNumber(view.reachEstimate.max)}
											</div>
										)
										: view.interests.length > 0
												|| view.countries.length > 0
												|| view.cities.length > 0
										? (
											<div className="text-sm text-gray-500">
												{t('targeting.addTargetingForReach')}
											</div>
										)
										: null}
								</div>
							)}
							<div className="col-span-2 flex flex-col pb-10 gap-2 items-center justify-center py-4">
								{validationError && !campaignQuota.isExhausted && (
									<div className="text-sm text-red-600 bg-red-50 px-4 py-2 rounded-md">
										{validationError}
									</div>
								)}

								<div>
									<Button
										onClick={events.onSave}
										type="committal"
										disabled={isInvalidCombination || view.isLaunching}
										isLoading={view.isLaunching}
									>
										{t('targeting.saveAndLaunch')}
									</Button>
								</div>
								<div className="text-sm text-grey-500">
									{t('targeting.reviewBeforeLaunch')}
								</div>
							</div>
						</div>
					)}
			</div>
		</CampaignNav>
	)
}
